import { title } from 'process';
import { api } from './api';
import { chatTenantService } from './chatTenantService';

export interface IntelligentChatRequest {
  query: string;
  threadId?: string;
  workspaceId?: string;
  conversationHistory?: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
  }>;
}

export interface IntelligentChatResponse {
  message: string;
  threadId: string;
  messageId?: string; // ID da mensagem do workspace chat
  documentReferences?: Array<{
    id: string;
    documentId: string;
    title: string;
    documentTitle: string;
    path: string;
    filePath: string;
    content: string;
    score: number;
    relevanceScore: number;
    metadata?: Record<string, any>;
  }>;
  intent?: {
    primaryIntent: string;
    confidence: number;
    reasoning: string;
  };
  searchResults?: Array<{
    documentId: string;
    content: string;
    score: number;
    metadata?: Record<string, any>;
  }>;
}

export interface StartIntelligentThreadRequest {
  workspaceId: string;
  title: string;
  initialMessage?: string;
}

export interface StartIntelligentThreadResponse {
  threadId: string;
  title: string;
  success: boolean;
}

export interface UpdateThreadRequest {
  threadId: string;
  title: string;
}

export interface UpdateThreadResponse {
  success: boolean;
  message?: string;
}

class IntelligentChatService {
  public baseUrl = '/chatindoc';

  /**
   * Inicia uma nova conversa inteligente usando a nova API ChatInDoc
   */
  async startIntelligentThread(request: StartIntelligentThreadRequest): Promise<StartIntelligentThreadResponse> {
    try {
      console.log('📡 Chamando API para criar thread:', request);
      
      const response = await api.post(`${this.baseUrl}/threads`, {
        workspaceId: request.workspaceId,
        title: request.title,
        ...chatTenantService.buildCustomerIdParams(),
      });

      console.log('📡 Resposta da API:', response.data);

      return {
        threadId: response.data.threadId, // Nova estrutura de resposta
        title: response.data.title,
        success: response.data.success
      };
    } catch (error: any) {
      console.error('❌ Erro na API de criar thread:', error);
      console.error('❌ Response data:', error.response?.data);
      throw new Error(error.response?.data?.error || 'Erro ao iniciar conversa');
    }
  }

  /**
   * Atualiza o título de uma thread existente usando a nova API ChatInDoc
   */
  async updateThread(request: UpdateThreadRequest): Promise<UpdateThreadResponse> {
    try {
      const response = await api.put(`${this.baseUrl}/threads`, {
        threadId: request.threadId,  // Usar threadId em vez de id
        title: request.title
      });
      
      return {
        success: true,
        message: 'Thread atualizada com sucesso'
      };
    } catch (error: any) {
      console.error('Erro ao atualizar thread:', error);
      throw new Error(error.response?.data?.error || 'Erro ao atualizar thread');
    }
  }

  /**
   * Deleta uma thread existente usando a nova API ChatInDoc
   */
  async deleteThread(threadId: string): Promise<UpdateThreadResponse> {
    try {
      const response = await api.delete(`${this.baseUrl}/threads/${threadId}`);
      
      return {
        success: true,
        message: 'Thread deletada com sucesso'
      };
    } catch (error: any) {
      console.error('Erro ao deletar thread:', error);
      throw new Error(error.response?.data?.error || 'Erro ao deletar thread');
    }
  }

  /**
   * Envia uma mensagem para uma thread específica usando a nova API ChatInDoc
   */
  async sendMessage(threadId: string, message: string, role: 'user' | 'assistant' = 'user'): Promise<any> {
    try {
      const response = await api.post(`${this.baseUrl}/threads/${threadId}/messages`, {
        message: message,
        role: role
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Erro ao enviar mensagem:', error);
      throw new Error(error.response?.data?.error || 'Erro ao enviar mensagem');
    }
  }

  /**
   * Envia mensagem e recebe resposta inteligente
   */
  async sendIntelligentMessage(request: IntelligentChatRequest): Promise<IntelligentChatResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/threads/${request.threadId}/messages`, {
        message: request.query,
        options: {
          enableIntelligence: true,
          includeDocumentReferences: true,
          includeIntentAnalysis: true
        }
      });

      return {
        message: response.data.message || response.data.content || 'Resposta não disponível',
        threadId: request.threadId || '',
        messageId: response.data.id, // ID da mensagem retornado pela API (GUID)
        documentReferences: response.data.documentReferences || [],
        intent: response.data.intent,
        searchResults: response.data.searchResults
      };
    } catch (error: any) {
      console.error('Erro ao enviar mensagem inteligente:', error);
      throw new Error(error.response?.data?.error || 'Erro ao processar mensagem');
    }
  }

  /**
   * Verifica saúde do backend
   */
  async checkBackendHealth(): Promise<{ isHealthy: boolean; message: string; existingThreads?: any[] }> {
    try {
      
      // Tentar acessar o endpoint de status para verificar se está funcionando
      const response = await api.get(`${this.baseUrl}/health`);
      
      return {
        isHealthy: true,
        message: 'Copiloto conectado e funcionando'
      };
    } catch (error) {
      console.error('checkBackendHealth - Erro ao verificar saúde:', error);
      return {
        isHealthy: false,
        message: 'Copiloto não está disponível'
      };
    }
  }

  /**
   * Carrega threads existentes do backend
   */
  async loadExistingThreads(workspaceId: string): Promise<any[]> {
    try {
      
      const response = await api.get(`${this.baseUrl}/workspaces/${workspaceId}/threads`, {
        params: chatTenantService.buildCustomerIdParams(),
      });
      
      return response.data || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Carrega mensagens de uma thread específica
   */
  async loadThreadMessages(threadId: string): Promise<any[]> {
    try {      
      const response = await api.get(`${this.baseUrl}/threads/${threadId}/history`);         
      return response.data || [];
    } catch (error) {     
      return [];
    }
  }
}

export const intelligentChatService = new IntelligentChatService();
